/**
 * Decodes the payload segment of a JWT WITHOUT verifying its signature.
 *
 * This is safe here specifically because every caller only reads a claim to
 * decide client-side behavior (e.g. which tenant to re-scope into) — the
 * backend independently re-verifies the signature on every request that
 * actually uses the token (`JwtAuthGuard`). Never use this to make an
 * authorization decision; it only reads what a token CLAIMS, not what a
 * server has confirmed.
 *
 * Uses `atob` (no extra dependency) with base64url → base64 normalization
 * (`-` → `+`, `_` → `/`, padded to a multiple of 4) and a `decodeURIComponent`
 * / `escape` round trip so multi-byte UTF-8 claims (e.g. accented names) come
 * back correctly instead of as mojibake.
 *
 * Returns `null` on any malformed input instead of throwing — a corrupt or
 * unexpected token shape must never crash the caller.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    const binary = atob(padded);
    const utf8 = decodeURIComponent(
      Array.from(binary, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );

    const payload: unknown = JSON.parse(utf8);
    // An array passes `typeof === 'object'`, but a JWT payload is never one.
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
    return payload as Record<string, unknown>;
  } catch {
    return null;
  }
}
